import { useState, useEffect } from 'react'
import Flex from 'components/priority/Flex/Flex'
import Icon from 'components/priority/Icon/Icon'
import Text from 'components/priority/Text/Text'
import startCase from 'lodash/startCase'
import capitalize from 'lodash/capitalize'
import * as Menus from '../Menus'
import * as S from './SlideoutMenu.styled'
import { default as _omit } from 'lodash/omit'
import {
  useRecoilCallback,
  useResetRecoilState,
  useRecoilState,
  useSetRecoilState,
} from 'recoil'
import {
  hasNewCaseAtom,
  caseAtom,
  caseFieldsAtom,
  caseValuesAtom,
  unsavedChangesAtom,
} from 'atoms'
import { useSetToast } from 'hooks'
import { useErrorHandler } from 'hooks/utility/useErrorHandler'
import { refreshTinyMCEEditors } from 'utils/tinyMCE/refreshTinyMCEs'
import { ensureDefaultService } from 'utils/caseDetails/ensureDefaultService'
import { flushAdditionalFamilyDraft } from 'utils/caseDetails/flushAdditionalFamilyDraft'
import { useTranslation } from 'react-i18next'
import { updateCase } from '@/api/cases'
import { nameOfDeceasedFromCase } from '@/utils/editor-text-box/editorTextBoxAdapter'
import { useLoadCaseValues } from '@/hooks/utility/useLoadCaseValues'
import { useEditorProbeLifetime } from '@/hooks/utility/useEditorProbeLifetime'

export const SlideoutMenu = ({
  menu,
  title,
  show: [show, setShow] = [false, () => {}],
  theme,
  themeOverride,
  basePages,
  baseLayout,
  setIsLoading,
  isSearchMenu,
  update,
  ...props
}) => {
  useEditorProbeLifetime(`SlideoutMenu:${menu || 'none'}`)
  const SelectedMenu = Menus?.[`${menu}Menu`]
  const { t } = useTranslation()
  const setToast = useSetToast()
  const handleError = useErrorHandler('SlideoutMenu')
  const resetHasNewCase = useResetRecoilState(hasNewCaseAtom)
  const [_case, setCase] = useRecoilState(caseAtom)
  const [pairs, setPairs] = useState([])
  const setUnsavedChanges = useSetRecoilState(unsavedChangesAtom)

  const loadInitialData = useLoadCaseValues()

  useEffect(() => {
    loadInitialData(pairs)

    //eslint-disable-next-line
  }, [pairs])

  const mapValuePairs = (_case) => {
    //map the case object into an array of object for each key/value
    const mapped = _case
      ? Object.keys(_case).map((key) => ({
          id: key,
          value: _case[key],
        }))
      : []
    setPairs(mapped)
  }

  useEffect(() => {
    const caseTmp = { ..._case }

    if (!caseTmp?.services) {
      caseTmp.services = []
    }
    if (caseTmp) {
      mapValuePairs(caseTmp)
    }
  }, [_case])

  const saveCase = useRecoilCallback(({ snapshot, set }) => async () => {
    // Persist any typed-but-not-Added additional-family entry — the payload
    // below is built from the pre-flush snapshot, so it is overridden after.
    const flushedAdditionalFamily = await flushAdditionalFamilyDraft({
      snapshot,
      set,
    })
    //get a snapshot of the recoil state (this is fuzzy to me still)
    //get the ids from the caseFieldsAtom atom
    const ids = await snapshot.getPromise(caseFieldsAtom)
    //create an empty array to store the returned values
    const allValues = []
    //extract the case_id for the patch
    let case_id = ''
    //this code came from the same sample listed above
    //for each id get the caseValue - which is the key/value object
    //push that to the array and save the case_id seperately
    for (const field of ids) {
      const caseValue = await snapshot.getPromise(caseValuesAtom(field))
      if (caseValue.id === 'id') case_id = caseValue.value
      allValues.push(caseValue)
    }
    //map the array of key/value objects back into one flat object
    const mapped = allValues.map((item) => ({ [item.id]: item.value }))
    const caseObj = Object.assign({}, ...mapped)
    //prep to send
    //the server will reject a payload with certain properties, this will remove those and then send the payload.
    const omit = [
      'id',
      'funeral_home_id',
      'created_at',
      'updated_at',
      'archived',
      'filledImages',
      'age',
      'age_years',
      'age_months',
      'age_days',
    ]
    const payload = _omit(caseObj, omit)
    payload.name_of_deceased = nameOfDeceasedFromCase(payload)
    if (flushedAdditionalFamily) {
      payload.additional_family = flushedAdditionalFamily
    }

    if (payload?.services?.length > 0) {
      payload.services = ensureDefaultService(payload.services)
    }

    if (case_id.length > 0) {
      try {
        const { data } = await updateCase(case_id, payload)
        //on response update the pairs which triggers the data load and also update the cases table so if they close out the modal it's up-to-date
        mapValuePairs(data)
        resetHasNewCase()
        if (update) {
          update(false, true)
        }
        setCase(data)
        refreshTinyMCEEditors({ refreshedCaseDetails: data })
        setToast({ text: t('toast.caseUpdatedSuccessfully') })
        setShow(false)
        setUnsavedChanges(false)
      } catch (error) {
        handleError(error?.response?.data?.message || error.message, error)
      }
    }
  })

  return (
    <S.SlideoutMenu
      data-testid="slideout-menu"
      top="0"
      left="calc(100% + 1px)"
      position="absolute"
      height="100%"
      width={show ? '470px' : '0'}
      background="white"
      transition=".25s"
      show={show}
      {...props}
    >
      <Flex column minWidth="470px" height="100%">
        <Flex
          justify="space-between"
          align="center"
          margin="24px 0"
          padding="8px 32px"
          gap="8px"
        >
          <Text weight="600" size="16px">
            {!isSearchMenu
              ? t(
                  `editor.menus.${capitalize(startCase(title))}`,
                  capitalize(startCase(title)),
                )
              : t('editor.searchResults')}
          </Text>
          <Icon
            clickable
            onClick={() => setShow(false)}
            icon="exitModal"
            fill="gray1"
            hoverfill="gray2"
          />
        </Flex>
        <S.Content column flex="1" overflowY="auto" overflowX="hidden">
          {SelectedMenu && (
            <SelectedMenu
              setShow={setShow}
              theme={theme}
              themeOverride={themeOverride}
              basePages={basePages}
              baseLayout={baseLayout}
              setIsLoading={setIsLoading}
              show={show}
              update={update}
              saveCase={saveCase}
              case_id={_case.id}
              name_of_deceased={_case.name_of_deceased}
            />
          )}
        </S.Content>
      </Flex>
    </S.SlideoutMenu>
  )
}

export default SlideoutMenu
